import type { MaterialData } from '../../project/types/materials';

/** Segment row for warehouse pipe pups (same shape as Pipe Segmentation modal). */
export type WarehousePipeSegment = {
  id: string;
  segmentName: string;
  segmentLength: number;
  dateInventory: string; // ISO date yyyy-mm-dd for form/API
  used: boolean;
};

const MAX_PUP_SEGMENTS = 26;

/** Whole pups count (pipe inventory). */
export function sanitizePupCount(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

/** Length fields cannot be negative. */
export function sanitizeNonNegativeLength(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
}

export function segmentNameForIndex(index: number): string {
  return String.fromCharCode(65 + index); // A, B, C, ...
}

/** Load edit form segments from API details; sizes list to Number of pups when set. */
export function buildInventorySegmentsFromMaterialDetails(
  details: Record<string, unknown> | undefined
): WarehousePipeSegment[] {
  if (!details) return [];
  const parsed = parsePipeSegmentationFromDetails(details);
  const extra = details.pipeExtraDetails as Record<string, unknown> | undefined;
  const pupRaw = extra?.numberOfPups;
  const count =
    pupRaw != null && pupRaw !== '' ? sanitizePupCount(pupRaw) : parsed.length;
  if (!Number.isFinite(count) || count <= 0) return parsed;
  const dateRaw = details.dateRecorded;
  const dateIso =
    typeof dateRaw === 'string' && dateRaw.trim()
      ? dateRaw.slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  return resizeWarehouseSegments(parsed, count, dateIso);
}

export function createEmptyWarehouseSegment(index: number, dateIso?: string): WarehousePipeSegment {
  return {
    id: `${Date.now()}-${index}`,
    segmentName: segmentNameForIndex(index),
    segmentLength: 0,
    dateInventory: dateIso || new Date().toISOString().slice(0, 10),
    used: false,
  };
}

/** Resize segment list to match Number of pups; keeps existing row edits when possible. */
export function resizeWarehouseSegments(
  existing: WarehousePipeSegment[],
  count: number,
  dateIso?: string
): WarehousePipeSegment[] {
  const n = Math.max(0, Math.min(MAX_PUP_SEGMENTS, sanitizePupCount(count)));
  if (n === 0) return [];
  const next = existing.slice(0, n).map((seg, i) => ({
    ...seg,
    segmentName: seg.segmentName?.trim() ? seg.segmentName : segmentNameForIndex(i),
  }));
  while (next.length < n) {
    next.push(createEmptyWarehouseSegment(next.length, dateIso));
  }
  return next;
}

export function parsePipeSegmentationFromDetails(
  details: Record<string, unknown> | undefined
): WarehousePipeSegment[] {
  const raw = details?.pipeSegmentation;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((segment: Record<string, unknown>, index: number) => {
    const dateRaw = segment.dateInventory;
    let dateInventory = new Date().toISOString().slice(0, 10);
    if (dateRaw instanceof Date && !Number.isNaN(dateRaw.getTime())) {
      dateInventory = dateRaw.toISOString().slice(0, 10);
    } else if (typeof dateRaw === 'string' && dateRaw.trim()) {
      const d = new Date(dateRaw);
      dateInventory = !Number.isNaN(d.getTime())
        ? d.toISOString().slice(0, 10)
        : dateRaw.slice(0, 10);
    }
    return {
      id: String(segment.id ?? `${index}`),
      segmentName: String(segment.segmentName ?? segmentNameForIndex(index)),
      segmentLength: sanitizeNonNegativeLength(segment.segmentLength),
      dateInventory,
      used: Boolean(segment.used),
    };
  });
}

/** Form state for Analysis warehouse pipe inventory (no project/pipe setup). */
export type WarehousePipeFormState = {
  warehouseProjectLabel: string;
  pipeNRI: string;
  pipeNo: string;
  pipePipeId: string;
  pipeHeatNumber: string;
  pipeLength: string;
  pipeOuterDiameter: string;
  pipeWallThickness: string;
  pipeGradeLabel: string;
  pipeMaterialTypeLabel: string;
  manufacturerLabel: string;
  pipeCondition: string;
  wbsNumber: string;
  contractor: string;
  numberOfPups: string;
  pipeSegmentation: WarehousePipeSegment[];
  pipeMarking: string;
  pipeNotes: string;
  mtrReceived: boolean;
  materialStatus: 'Available' | 'Consumed';
  materialDate: string;
  dateRecorded: string;
  pipeProjectStage: 'PQT' | 'Production' | '';
};

export const emptyWarehousePipeForm = (): WarehousePipeFormState => ({
  warehouseProjectLabel: '',
  pipeNRI: '',
  pipeNo: '',
  pipePipeId: '',
  pipeHeatNumber: '',
  pipeLength: '',
  pipeOuterDiameter: '',
  pipeWallThickness: '',
  pipeGradeLabel: '',
  pipeMaterialTypeLabel: '',
  manufacturerLabel: '',
  pipeCondition: '',
  wbsNumber: '',
  contractor: '',
  numberOfPups: '',
  pipeSegmentation: [],
  pipeMarking: '',
  pipeNotes: '',
  mtrReceived: false,
  materialStatus: 'Available',
  materialDate: new Date().toISOString().slice(0, 10),
  dateRecorded: new Date().toISOString().slice(0, 10),
  pipeProjectStage: 'Production',
});

export function isWarehouseMaterialRow(row: Record<string, unknown>): boolean {
  if (row.isWarehouseInventory === true) return true;
  const details = row.details as Record<string, unknown> | undefined;
  if (details?.isWarehouseInventory === true) return true;

  const projectId = row.projectId ?? row.project_id ?? details?.projectId;
  const pipeId = row.pipeId ?? row.pipe_id ?? details?.pipeId;
  const hasProject = projectId != null && projectId !== '';
  const hasPipe = pipeId != null && pipeId !== '';
  return !hasProject && !hasPipe && row.materialCategory === 'Pipe';
}

export function warehouseFormFromMaterial(row: MaterialData): WarehousePipeFormState {
  const r = row as MaterialData & {
    warehouseProjectLabel?: string;
    projectName?: string;
  };
  const d = (row.details ?? {}) as Record<string, unknown>;
  const extra = (d.pipeExtraDetails ?? {}) as Record<string, unknown>;
  const pipeSegmentation = parsePipeSegmentationFromDetails(d);
  return {
    warehouseProjectLabel: String(
      r.warehouseProjectLabel ?? d.warehouseProjectLabel ?? r.projectName ?? ''
    ),
    pipeNRI: String(d.pipeNRI ?? ''),
    pipeNo: String(d.pipeNo ?? ''),
    pipePipeId: String(d.pipePipeId ?? ''),
    pipeHeatNumber: String(d.pipeHeatNumber ?? ''),
    pipeLength: d.pipeLength != null ? String(d.pipeLength) : '',
    pipeOuterDiameter: d.pipeOuterDiameter != null ? String(d.pipeOuterDiameter) : '',
    pipeWallThickness: d.pipeWallThickness != null ? String(d.pipeWallThickness) : '',
    pipeGradeLabel: String(d.pipeGradeLabel ?? extra.pipeGradeLabel ?? ''),
    // Prefer material type label; fall back to legacy pipeType free-text
    pipeMaterialTypeLabel: String(
      d.pipeMaterialTypeLabel ?? d.pipeType ?? d.pipeTypeLabel ?? ''
    ),
    manufacturerLabel: String(d.manufacturerLabel ?? extra.manufacturerLabel ?? ''),
    pipeCondition: String(d.pipeCondition ?? extra.pipeCondition ?? ''),
    // Prefer WBS; accept legacy wpsNumber from earlier warehouse saves
    wbsNumber: String(extra.wbsNumber ?? extra.wpsNumber ?? ''),
    contractor: String(extra.contractor ?? ''),
    numberOfPups:
      extra.numberOfPups != null
        ? String(extra.numberOfPups)
        : pipeSegmentation.length
          ? String(pipeSegmentation.length)
          : '',
    pipeSegmentation,
    pipeMarking: String(d.pipeMarking ?? ''),
    pipeNotes: String(d.pipeNotes ?? ''),
    mtrReceived: Boolean(d.mtrReceived),
    materialStatus: (row.materialStatus as 'Available' | 'Consumed') || 'Available',
    materialDate: row.materialDate
      ? String(row.materialDate).slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    dateRecorded: d.dateRecorded
      ? String(d.dateRecorded).slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    pipeProjectStage:
      d.pipeProjectStage === 'PQT' || d.pipeProjectStage === 'Production'
        ? d.pipeProjectStage
        : 'Production',
  };
}

function numOrUndefined(v: string): number | undefined {
  const trimmed = v.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

export function buildWarehouseCreatePayload(form: WarehousePipeFormState) {
  const pipeNo = form.pipeNo.trim();
  if (!pipeNo) {
    throw new Error('PIPE_NO_REQUIRED');
  }
  const pipeHeatNumber = form.pipeHeatNumber.trim();
  if (!pipeHeatNumber) {
    throw new Error('HEAT_NUMBER_REQUIRED');
  }

  return {
    projectId: null,
    pipeId: null,
    data: [
      {
        materialCategory: 'Pipe' as const,
        materialStatus: form.materialStatus,
        materialDate: form.materialDate ? new Date(form.materialDate).toISOString() : new Date().toISOString(),
        isWarehouseInventory: true,
        warehouseProjectLabel: form.warehouseProjectLabel.trim() || null,
        projectId: null,
        pipeId: null,
        projectTypeId: null,
        details: {
          pipeNRI: form.pipeNRI.trim() || null,
          pipeNo,
          pipePipeId: form.pipePipeId.trim() || null,
          pipeProjectStage: form.pipeProjectStage || 'Production',
          pipeHeatNumber,
          pipeLength:
            form.pipeLength === '' || form.pipeLength == null
              ? undefined
              : sanitizeNonNegativeLength(form.pipeLength),
          pipeOuterDiameter: numOrUndefined(form.pipeOuterDiameter),
          pipeWallThickness: numOrUndefined(form.pipeWallThickness),
          pipeGradeLabel: form.pipeGradeLabel.trim() || null,
          pipeMaterialTypeLabel: form.pipeMaterialTypeLabel.trim() || null,
          manufacturerLabel: form.manufacturerLabel.trim() || null,
          pipeCondition: form.pipeCondition.trim() || null,
          pipeMarking: form.pipeMarking.trim() || null,
          pipeNotes: form.pipeNotes.trim() || null,
          mtrReceived: form.mtrReceived,
          dateRecorded: form.dateRecorded
            ? new Date(form.dateRecorded).toISOString()
            : null,
          isWarehouseInventory: true,
          pipeSegmentation:
            form.pipeSegmentation.length > 0
              ? form.pipeSegmentation.map((seg) => ({
                  id: seg.id,
                  segmentName: seg.segmentName,
                  segmentLength: sanitizeNonNegativeLength(seg.segmentLength),
                  dateInventory: seg.dateInventory
                    ? new Date(seg.dateInventory).toISOString()
                    : new Date().toISOString(),
                  used: seg.used,
                }))
              : undefined,
          pipeExtraDetails: {
            wbsNumber: form.wbsNumber.trim() || undefined,
            contractor: form.contractor.trim() || undefined,
            manufacturerLabel: form.manufacturerLabel.trim() || undefined,
            numberOfPups:
              form.numberOfPups === '' || form.numberOfPups == null
                ? undefined
                : sanitizePupCount(form.numberOfPups),
          },
        },
      },
    ],
  };
}

export function buildWarehouseUpdatePayload(materialId: number, form: WarehousePipeFormState) {
  const base = buildWarehouseCreatePayload(form);
  return {
    data: [
      {
        ...base.data[0],
        materialId,
      },
    ],
  };
}

export type WarehouseUploadedDocument = {
  fileName: string;
  s3Key: string;
  version: number;
  tags: string[];
  uploadedBy: string;
};

export function mergeWarehouseDocuments<
  T extends { data: Array<Record<string, unknown>> },
>(payload: T, documents: WarehouseUploadedDocument[]): T {
  if (!documents.length) return payload;
  payload.data[0] = {
    ...payload.data[0],
    documents,
  };
  return payload;
}
